import { useCallback, useEffect, useMemo, useState } from 'react';
import Hls from 'hls.js';
import { isTreatError } from '../utils/training/checkAudioError';

// 本カスタムフックの返り値
export type UseAudioValue = {
  // 音声ファイル読み込み
  loadAudio: (srcUrl: string) => void;
  // 再生
  play: () => void;
  // 一時停止
  pause: () => void;
  // 再生中か
  isPlaying: boolean;
  // ループ変更
  toggleLoop: () => void;
  // ループ中か
  isLoop: boolean;
  // 再生時間
  currentTime: number;
  // 音声の先頭
  leadAudio: () => void;
  // 再再生準備
  prepareReplay: () => void;
  // 再再生
  replay: () => void;
  // 再生完了
  completeAudio: () => void;
  // 再生速度
  playBackRate: number;
  // 再生速度変更
  changePlayBackRate: (rate: number) => void;
  // 再生時間変更
  changeCurrentTime: (time: number) => void;
  // 最初のスタート時の再生時間
  changeStartCurrentTime: (time: number) => void;
  // 終了したかどうか
  isEnded: boolean;
  // 一度再生済みか
  isFirstPlayed: boolean;
  // エラー
  isError: boolean;
  // エラーの変更メソッド
  changeIsError: (vaule: boolean) => void;
};

const useAudio = (): UseAudioValue => {
  // audio要素
  const [audio] = useState(new Audio());
  // hlsjs
  const [hls] = useState(new Hls());
  // 再生中か
  const [, setIsPlaying] = useState<boolean>(false);
  // ループ
  const [, setIsLoop] = useState<boolean>(false);
  // 再生終了したか
  const [isEnded, setIsEnded] = useState<boolean>(false);
  // 再生速度
  const [playBackRate, setPlayBackRate] = useState<number>(
    parseFloat(sessionStorage.getItem('playBackRate') ?? '1.0'),
  );
  // 現在の再生時間
  const [, setCurrentTime] = useState<number>(0);
  // 最初の再生がされた
  const [isFirstPlayed, setIsFirstPlayed] = useState<boolean>(false);
  // スタート時のcurrentTime
  const [startCurrentTime, setStartCurrentTime] = useState<number>(0);

  // LoadError
  const [isError, setIsError] = useState<boolean>(false);

  /** Method */

  /**
   * プレーンなaudioタグを使用するかどうか
   * @returns YES=使う/NO=使わない
   */
  const isUsePlaneAudio = useMemo(
    (): boolean =>
      !Hls.isSupported() &&
      audio.canPlayType('application/vnd.apple.mpegurl').length !== 0,
    [audio],
  );

  /**
   * 再生時間を変更する
   * @param time 変更する時間
   */
  const changeCurrentTime = useCallback(
    (time: number) => {
      audio.currentTime = time;
      setCurrentTime(time);
    },
    [audio],
  );

  /**
   * 再生
   */
  const play = useCallback(() => {
    /**
     * Hlsjsで再生する場合は、ここで再生時間を変更する
     */
    if (Hls.isSupported() && !isFirstPlayed) {
      changeCurrentTime(startCurrentTime);
      setIsFirstPlayed(true);
    }
    setIsEnded(false);
    if (audio.paused) {
      void audio.play();
    }
  }, [audio, changeCurrentTime, isFirstPlayed, startCurrentTime]);

  /**
   * 一時停止
   */
  const pause = useCallback(() => {
    if (!audio.paused) {
      audio.pause();
    }
  }, [audio]);

  /**
   * 音声の先頭
   */
  const leadAudio = useCallback(() => {
    audio.currentTime = 0.0;
  }, [audio]);

  /**
   * 再再生の準備
   */
  const prepareReplay = useCallback(() => {
    // endフラグ解除
    setIsEnded(false);
    // 再生位置を最初に戻す
    leadAudio();
  }, [leadAudio]);

  /**
   * 再スタート
   */
  const replay = useCallback(() => {
    // 再再生の準備
    prepareReplay();
    // 再生
    play();
  }, [play, prepareReplay]);

  /**
   * 再生完了
   */
  const completeAudio = useCallback(() => {
    audio.pause();
    audio.currentTime = 0.0;
    setIsEnded(true);
  }, [audio]);

  /**
   * ループ変更
   */
  const toggleLoop = useCallback(() => {
    audio.loop = !audio.loop;
    // レンダリングするために、変更する
    setIsLoop(audio.loop);
  }, [audio]);

  /**
   * 再生速度変更
   * @param rate 変更する値
   */
  const changePlayBackRate = useCallback(
    (rate: number) => {
      audio.playbackRate = rate;
      setPlayBackRate(rate);
      sessionStorage.setItem('playBackRate', String(rate));
    },
    [audio],
  );

  /**
   * 開始時の再生時間を変更する
   * @param time 設定する時間
   */
  const changeStartCurrentTime = useCallback((time: number) => {
    setStartCurrentTime(time);
  }, []);

  /**
   * isErrorの値を変更する
   * @param value 設定する値
   */
  const changeIsError = useCallback((value: boolean) => {
    setIsError(value);
  }, []);

  /**
   * 音声ロード
   */
  const loadAudio = useCallback(
    (srcUrl: string) => {
      if (Hls.isSupported()) {
        hls.loadSource(srcUrl);
        hls.attachMedia(audio);

        // エラーのリスナー
        hls.on(Hls.Events.ERROR, (_event, data) => {
          // エラー扱いにする場合
          if (isTreatError(data)) {
            changeIsError(true);
          }
        });
      } else if (audio.canPlayType('application/vnd.apple.mpegurl')) {
        // プレーンなaudioタグで再生できる場合
        audio.src = srcUrl;
      }
    },
    [audio, hls, changeIsError],
  );

  /** Event */
  /**
   * play時のイベント
   */
  audio.onplay = () => {
    setIsPlaying(!audio.paused);
  };

  /**
   * pause時のイベント
   */
  audio.onpause = () => {
    setIsPlaying(!audio.paused);
  };

  /**
   * end時のイベント
   */
  audio.onended = () => {
    if (!audio.loop) {
      setIsPlaying(!audio.paused);
      setIsEnded(true);
    }
  };

  // 通常だと約0.25秒(chromeの場合)にしか
  // timeupdateが呼ばれないため、リフレッシュレート時に、強制的に呼び出す。
  // （センテンスの表示/非表示更新時に、currentTimeをチェックするので、リフレッシュレートでも問題ない）
  // 参考
  // - https://lowreal.net/2016/06/04/1
  // - https://ics.media/entry/210414/
  audio.onloadedmetadata = () => {
    let time = audio.currentTime;
    // timeupdateを発火させる処理
    // ここでメソッド宣言している理由
    // 1. このメソッドを「onloadmedata」の外に出して宣言すると、「let time = audio.currentTime;」も外に出す必要があり、スコープが広くなりすぎるため
    // 2. metadataをloadしてから、currentTimeを取得した方が良いと考えたため
    const fireTimeUpdate = () => {
      if (time !== audio.currentTime) {
        time = audio.currentTime;
        audio.dispatchEvent(new Event('timeupdate'));
      }
      requestAnimationFrame(fireTimeUpdate);
    };

    fireTimeUpdate();
  };

  /**
   * 再生可能時
   */
  audio.oncanplay = () => {
    // プレーンaudioタグ（基本iOS）の場合は、再生位置を調整する
    if (isUsePlaneAudio && !isFirstPlayed) {
      changeCurrentTime(startCurrentTime);
      setIsFirstPlayed(true);
    }
  };

  /**
   * エラー
   */
  audio.onerror = () => {
    if (Hls.isSupported()) {
      // Hlsjs側で処理するので何もしない
      return;
    }
    changeIsError(true);
  };

  /**
   * currentTIme変更イベント
   */
  audio.ontimeupdate = () => {
    const time = audio.currentTime;
    setCurrentTime(time);
  };

  useEffect(() => {
    audio.defaultPlaybackRate = playBackRate;

    // イベントリスナーをnullにする。
    return () => {
      hls.removeAllListeners();
      audio.onloadedmetadata = null;
      audio.ontimeupdate = null;
      audio.onplay = null;
      audio.onpause = null;
      audio.onended = null;
      audio.onerror = null;
      audio.oncanplay = null;
      pause();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    loadAudio,
    play,
    pause,
    isPlaying: !audio.paused,
    toggleLoop,
    isLoop: audio.loop,
    currentTime: audio.currentTime,
    leadAudio,
    completeAudio,
    prepareReplay,
    replay,
    playBackRate,
    changePlayBackRate,
    changeCurrentTime,
    changeStartCurrentTime,
    isEnded,
    isFirstPlayed,
    isError,
    changeIsError,
  };
};

export default useAudio;
